import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Car, Bike, Send, CheckCircle, FileText, Upload, X, Image, Building } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  'Lavage',
  'Pneumatique',
  'Carrosserie & Peinture',
  'Mécanique',
  'Réparation Jantes',
  'Vitrage & Pare-brise',
  'Covering',
  'Préparation Esthétique',
  'Réparation Cuir',
  'Contrôle Technique',
  'Dépannage & Remorquage',
  'Autre'
];

export default function QuotePage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteId, setQuoteId] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  const [formData, setFormData] = useState({
    service_type: '',
    vehicle_type: 'auto',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_plate: '',
    vehicle_mileage: '',
    description: '',
    firstname: '',
    name: '',
    company: '',
    email: '',
    phone: '',
    address: ''
  });

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 5 files max
    if (attachments.length + files.length > 5) {
      toast.error('Maximum 5 fichiers autorisés');
      return;
    }
    
    setUploadingFiles(true);
    const uploadedUrls = [];
    
    for (const file of files) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 10MB)`);
        continue;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: type de fichier non autorisé`);
        continue;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const res = await axios.post(`${API}/quotes/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push({
          filename: res.data.filename,
          url: res.data.url,
          originalName: file.name
        });
      } catch (error) {
        toast.error(`Erreur lors de l'upload de ${file.name}`);
      }
    }
    
    setAttachments([...attachments, ...uploadedUrls]);
    setUploadingFiles(false);
    
    if (uploadedUrls.length > 0) {
      toast.success(`${uploadedUrls.length} fichier(s) ajouté(s)`);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation : adresse + contacts + plaque OBLIGATOIRES partout
    if (!formData.address || !formData.firstname || !formData.name || !formData.phone || !formData.email) {
      toast.error('Tous les champs (prenom, nom, telephone, email, adresse) sont obligatoires');
      return;
    }
    if (!formData.vehicle_plate) {
      toast.error('La plaque d\u2019immatriculation est obligatoire pour toute demande de devis');
      return;
    }

    // Validation specifique carrosserie/peinture : 3 photos minimum + km
    const isCarrosserie = formData.service_type?.toLowerCase().includes('carrosserie') || formData.service_type?.toLowerCase().includes('peinture');
    // Validation specifique jantes : photos obligatoires (1 par jante minimum -> 4 recommandees)
    const isJantes = formData.service_type?.toLowerCase().includes('jante');

    if (isCarrosserie) {
      if (attachments.length < 3) {
        toast.error('Pour un devis carrosserie/peinture, joignez au moins 3 photos (vue 1m50, vue 50cm, et profil si enfoncement)');
        return;
      }
      if (!formData.vehicle_mileage) {
        toast.error('Le kilometrage est obligatoire pour un devis carrosserie/peinture');
        return;
      }
    }

    if (isJantes) {
      if (attachments.length < 4) {
        toast.error('Pour un devis Réparation Jantes, joignez au moins 4 photos (une par jante avec l\u2019état actuel)');
        return;
      }
    }

    setLoading(true);
    try {
      // Include attachment URLs in the form data
      const submitData = {
        ...formData,
        attachments: attachments.map(a => a.url)
      };
      
      const res = await axios.post(`${API}/quotes`, submitData);
      setQuoteId(res.data.id);
      setSubmitted(true);
      toast.success('Demande de devis envoyée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    const pdfUrl = `${API}/quotes/${quoteId}/pdf`;
    return (
      <div className="min-h-screen bg-[#09090B] py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-zinc-900/50 border-zinc-800 text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-500" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Demande Envoyée !</h2>
              <p className="text-zinc-400 mb-4">
                Votre demande de devis a bien été enregistrée. Vous recevez aussi un email avec le récapitulatif PDF en pièce jointe.
              </p>
              <div className="bg-zinc-800/50 p-4 rounded-lg mb-6">
                <p className="text-zinc-500 text-sm">Numéro de référence</p>
                <p className="text-red-500 font-mono font-bold">{quoteId.slice(0, 8).toUpperCase()}</p>
              </div>
              <p className="text-zinc-500 text-sm mb-6">
                Nous vous recontacterons sous 24-48h avec une estimation détaillée.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="quote-download-pdf">
                  <Button className="bg-cyan-600 hover:bg-cyan-700 w-full sm:w-auto">
                    <FileText size={18} className="mr-2" />
                    Télécharger ma demande (PDF)
                  </Button>
                </a>
                <Button
                  onClick={() => { setSubmitted(false); setAttachments([]); setFormData({ service_type: '', vehicle_type: 'auto', vehicle_brand: '', vehicle_model: '', vehicle_year: '', vehicle_plate: '', vehicle_mileage: '', description: '', firstname: '', name: '', company: '', email: '', phone: '', address: '' }); }}
                  className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                  data-testid="quote-new-request"
                >
                  Nouvelle demande
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="text-red-500" size={32} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            DEMANDER UN <span className="text-red-500">DEVIS</span>
          </h1>
          <p className="text-zinc-400">Décrivez votre besoin et recevez une estimation gratuite sous 24h</p>
        </div>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText size={20} className="text-red-500" />
              Formulaire de devis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Service Type */}
              <div>
                <Label className="text-zinc-300">Type de service *</Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={(v) => setFormData({...formData, service_type: v})}
                  required
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-2" data-testid="quote-service-type">
                    <SelectValue placeholder="Sélectionnez un service" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {SERVICE_TYPES.map((type) => (
                      <SelectItem key={type} value={type} className="text-white">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle Type */}
              <div>
                <Label className="text-zinc-300">Type de véhicule *</Label>
                <div className="flex gap-4 mt-2">
                  <Button
                    type="button"
                    variant={formData.vehicle_type === 'auto' ? 'default' : 'outline'}
                    className={formData.vehicle_type === 'auto' ? 'bg-red-600' : 'border-zinc-700'}
                    onClick={() => setFormData({...formData, vehicle_type: 'auto'})}
                  >
                    <Car className="mr-2" size={18} /> Auto
                  </Button>
                  <Button
                    type="button"
                    variant={formData.vehicle_type === 'moto' ? 'default' : 'outline'}
                    className={formData.vehicle_type === 'moto' ? 'bg-red-600' : 'border-zinc-700'}
                    onClick={() => setFormData({...formData, vehicle_type: 'moto'})}
                  >
                    <Bike className="mr-2" size={18} /> Moto
                  </Button>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-300">Marque *</Label>
                  <Input
                    required
                    placeholder="Ex: Renault"
                    value={formData.vehicle_brand}
                    onChange={(e) => setFormData({...formData, vehicle_brand: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white mt-2"
                    data-testid="quote-brand"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Modèle *</Label>
                  <Input
                    required
                    placeholder="Ex: Clio"
                    value={formData.vehicle_model}
                    onChange={(e) => setFormData({...formData, vehicle_model: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white mt-2"
                    data-testid="quote-model"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Année</Label>
                  <Input
                    placeholder="Ex: 2020"
                    value={formData.vehicle_year}
                    onChange={(e) => setFormData({...formData, vehicle_year: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white mt-2"
                    data-testid="quote-year"
                  />
                </div>
              </div>

              {/* Vehicle plate - OBLIGATOIRE pour TOUTE demande de devis */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Plaque d'immatriculation *</Label>
                  <Input
                    required
                    placeholder="AB-123-CD"
                    value={formData.vehicle_plate}
                    onChange={(e) => setFormData({...formData, vehicle_plate: e.target.value.toUpperCase()})}
                    className="bg-zinc-950 border-zinc-800 text-white mt-2"
                    data-testid="quote-vehicle-plate"
                  />
                </div>
                {/* Kilometrage : obligatoire seulement pour carrosserie/peinture */}
                {(formData.service_type?.toLowerCase().includes('carrosserie') || formData.service_type?.toLowerCase().includes('peinture')) && (
                  <div>
                    <Label className="text-zinc-300">Kilometrage du vehicule *</Label>
                    <Input
                      required
                      type="number"
                      min="0"
                      placeholder="ex: 85000"
                      value={formData.vehicle_mileage}
                      onChange={(e) => setFormData({...formData, vehicle_mileage: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white mt-2"
                      data-testid="quote-vehicle-mileage"
                    />
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <Label className="text-zinc-300">Description de votre besoin *</Label>
                <Textarea
                  required
                  placeholder="Décrivez le plus précisément possible votre demande (travaux souhaités, problème constaté, etc.)"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white mt-2 min-h-[120px]"
                  data-testid="quote-description"
                />
              </div>

              {/* File Attachments */}
              <div>
                <Label className="text-zinc-300">
                  Photos / Documents
                  {(formData.service_type?.toLowerCase().includes('carrosserie') || formData.service_type?.toLowerCase().includes('peinture') || formData.service_type?.toLowerCase().includes('jante')) ? ' *' : ' (optionnel)'}
                </Label>
                {(formData.service_type?.toLowerCase().includes('carrosserie') || formData.service_type?.toLowerCase().includes('peinture')) ? (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mt-2 mb-3">
                    <p className="text-orange-300 text-sm font-semibold mb-2">Photos obligatoires (3 prises de vue minimum) :</p>
                    <ul className="text-orange-200/80 text-xs space-y-1 list-disc list-inside">
                      <li>Une photo prise a environ <strong>1m50</strong> (vue d'ensemble)</li>
                      <li>Une photo prise a environ <strong>50 cm</strong> (gros plan du dommage)</li>
                      <li>S'il y a un enfoncement : une photo prise <strong>de profil</strong></li>
                    </ul>
                  </div>
                ) : formData.service_type?.toLowerCase().includes('jante') ? (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mt-2 mb-3">
                    <p className="text-cyan-300 text-sm font-semibold mb-2">Photos obligatoires (4 prises de vue minimum) :</p>
                    <ul className="text-cyan-200/80 text-xs space-y-1 list-disc list-inside">
                      <li>Une photo <strong>par jante</strong> (avant gauche, avant droite, arriere gauche, arriere droite)</li>
                      <li>Mettre en evidence les <strong>rayures, eclats, deformations ou peinture ecaillee</strong></li>
                      <li>Si jante voilée : photo prise <strong>de face</strong> pour mesurer l'amplitude</li>
                    </ul>
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm mt-1 mb-3">
                    Ajoutez des photos de votre véhicule ou des dommages pour une estimation plus précise (max 5 fichiers, 10MB chacun)
                  </p>
                )}
                
                {/* Upload Area */}
                <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-red-500/50 transition-colors">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="quote-file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="mx-auto text-zinc-500 mb-2" size={32} />
                    <p className="text-zinc-400 text-sm">
                      {uploadingFiles ? 'Upload en cours...' : 'Cliquez ou glissez vos fichiers ici'}
                    </p>
                    <p className="text-zinc-600 text-xs mt-1">JPG, PNG, GIF, WEBP ou PDF</p>
                  </label>
                </div>

                {/* Uploaded Files Preview */}
                {attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {attachments.map((file, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3"
                        data-testid={`attachment-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-600/20 rounded flex items-center justify-center">
                            <Image className="text-red-500" size={20} />
                          </div>
                          <span className="text-zinc-300 text-sm truncate max-w-[200px]">
                            {file.originalName}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                          className="text-zinc-500 hover:text-red-500"
                          data-testid={`remove-attachment-${index}`}
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="border-t border-zinc-800 pt-6">
                <h3 className="text-white font-bold mb-4">Vos coordonnées</h3>
                {/* Société (optionnel) */}
                <div className="mb-4">
                  <Label className="text-zinc-300">Société <span className="text-zinc-500 text-xs">(optionnel - pour les professionnels)</span></Label>
                  <div className="relative mt-2">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                    <Input
                      placeholder="Nom de votre société"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white pl-10"
                      data-testid="quote-company"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Prenom *</Label>
                    <Input
                      required
                      placeholder="Votre prenom"
                      value={formData.firstname}
                      onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white mt-2"
                      data-testid="quote-firstname"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Nom *</Label>
                    <Input
                      required
                      placeholder="Votre nom"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white mt-2"
                      data-testid="quote-name"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-zinc-300">Téléphone *</Label>
                    <Input
                      required
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white mt-2"
                      data-testid="quote-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Email *</Label>
                    <Input
                      required
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white mt-2"
                      data-testid="quote-email"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-zinc-300">
                    Adresse complete *
                    <span className="text-zinc-500 text-xs ml-2">(saisie assistee)</span>
                  </Label>
                  <div className="mt-2">
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(v) => setFormData((p) => ({ ...p, address: v }))}
                      onSelect={(s) => setFormData((p) => ({ ...p, address: s.label }))}
                      required
                      placeholder="N° de rue, voie, code postal, ville"
                      testid="quote-address"
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700 h-12"
                disabled={loading}
                data-testid="submit-quote"
              >
                {loading ? 'Envoi en cours...' : (
                  <>
                    <Send className="mr-2" size={18} />
                    Envoyer la demande
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
